import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AnalyticsModule } from '@flyfreely-portal-ui/analytics';
import { EnhancedHelpModule } from '@flyfreely-portal-ui/enhanced-help';
import { FeaturesModule } from '@flyfreely-portal-ui/features';
import { SharedUiModule } from '@flyfreely-portal-ui/ui';
import { ValidationModule } from '@flyfreely-portal-ui/validation';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormlyModule } from '@ngx-formly/core';
import { AttachmentsModule } from 'libs/attachments/src/lib/attachments.module';
import { InlineModalModule } from 'libs/inline-modal/src/lib/inline-modal.module';
import { LoadingIndicatorModule } from 'libs/loading-indicator/src/lib/loading-indicator.module';
import { MapModule } from 'libs/map/src/lib/map.module';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgxLoadingModule } from 'ngx-loading-reloaded';
import { AirspaceAuthorisationsDirective } from './airspace-authorisations.directive';
import { AirspaceCheckResultDirective } from './airspace-check-result.directive';
import { AirspaceCheckDetailsDialogue } from './airspace-check/airspace-check-details/airspace-check-details-dialogue.component';
import { AirspaceCheckResults } from './airspace-check/airspace-check-results.component';
import { AirspaceCheck } from './airspace-check/airspace-check.component';
import { JurisdictionLibraryDialogue } from './airspace-check/jurisdiction-library/jurisdiction-library.component';
import { JurisdictionLinksComponent } from './airspace-check/jurisdiction-links/jurisdiction-links.component';
import { MapAirspaceChecker } from './airspace-check/map-airspace-checker/map-airspace-checker.component';
import { MapAirspaceCheckerService } from './airspace-check/map-airspace-checker/map-airspace-checker.service';
import { MapAirspaceParametersComponent } from './airspace-check/map-airspace-parameters/map-airspace-parameters.component';
import { AirspaceCheckStatusIndicator } from './airspace-check/status-indicator/airspace-check-status-indicator.component';
import { InlineAirspaceWarningsAndBlockersComponent } from './airspace-check/warnings-and-blockers/inline-warnings-and-blockers.component';
import { AccountAirspaceDataComponent } from './airspace-data-settings/account-airspace-data.component';
import { AirspaceDialoguesService } from './airspace-dialogues.service';
import { AirspaceUnavailableAlert } from './airspace-unavailable-alert/airspace-unavailable-alert.component';
import { AuthorisationOutcomeDetailsDialogue } from './authorisation-outcome-details/authorisation-outcome-details-dialogue.component';
import { AirspaceAuthorisationAcknowledgementDialogue } from './authorisation/authorisation-agreement/authorisation-agreement-dialogue.component';
import { AirspaceAuthorisationMessagingComponent } from './authorisation/authorisation-messaging/airspace-authorisation-messaging.component';
import { AirspaceConsentForTrialDialogue } from './authorisation/authorisation-permissions/casa-consent-for-trial/airspace-consent-for-trial.component';
import { AirspaceAuthorisationPreviewDialogue } from './authorisation/authorisation-preview/airspace-authorisation-preview.component';
import { AuthorisationExistingDetailComponent } from './authorisation/authorisation-preview/authorisation-existing-detail/authorisation-existing-detail.component';
import { AuthorisationFooterComponent } from './authorisation/authorisation-preview/authorisation-footer/authorisation-footer.component';
import { AirspaceAuthorisationStatus } from './authorisation/authorisation-status/authorisation-status.component';
import { AirspaceAuthorisationSummary } from './authorisation/authorisation-summary/airspace-authorisation-summary.component';
import { InlineAuthorisationComponent } from './authorisation/inline-authorisation/inline-authorisation.component';
import { RemotePilotRegistrationDialogue } from './authorisation/remote-pilot-registration/remote-pilot-registration.component';
import { AirspaceDisclaimerDialogue } from './disclaimer/airspace-disclaimer-dialogue.component';
import { FormatAuthorisationEligibilityPipe } from './formatters/format-authorisation-eligibility.pipe';
import { FormatAuthorisationStatusPipe } from './formatters/format-authorisation-status.pipe';
import { AirspaceNotificationCard } from './notifications/airspace-notification.component';
import { AirspaceRuleDetailsDialogue } from './rule-details-dialogue/airspace-rule-details-dialogue.component';
import { AirspaceRuleMessageDialogue } from './rule-details-dialogue/rule-message/airspace-rule-message-dialogue.component';
import { ShowErrorsComponent } from './show-errors/show-errors.component';

@NgModule({
    imports: [
        CommonModule,
        SharedUiModule,
        FormsModule,
        ReactiveFormsModule,
        ModalModule.forChild(),
        InlineModalModule,
        PopoverModule,
        TooltipModule,
        AttachmentsModule,
        MapModule,
        NgxLoadingModule,
        ValidationModule,
        AnalyticsModule,
        FormlyModule,
        BsDatepickerModule,
        TimepickerModule,
        EnhancedHelpModule,
        FeaturesModule,
        CollapseModule,
        LoadingIndicatorModule,
        NgSelectModule
    ],
    providers: [AirspaceDialoguesService],
    declarations: [
        AirspaceRuleDetailsDialogue,
        AirspaceRuleMessageDialogue,
        AirspaceDisclaimerDialogue,
        AirspaceNotificationCard,
        AirspaceCheck,
        AirspaceCheckStatusIndicator,
        JurisdictionLibraryDialogue,
        AirspaceAuthorisationPreviewDialogue,
        AuthorisationExistingDetailComponent,
        AuthorisationFooterComponent,
        AirspaceConsentForTrialDialogue,
        AirspaceAuthorisationAcknowledgementDialogue,
        AirspaceAuthorisationSummary,
        AirspaceUnavailableAlert,
        AirspaceAuthorisationStatus,
        RemotePilotRegistrationDialogue,
        AirspaceAuthorisationMessagingComponent,
        JurisdictionLinksComponent,
        AirspaceCheckResultDirective,
        AirspaceCheckDetailsDialogue,
        FormatAuthorisationEligibilityPipe,
        InlineAirspaceWarningsAndBlockersComponent,
        AirspaceAuthorisationsDirective,
        InlineAuthorisationComponent,
        FormatAuthorisationEligibilityPipe,
        FormatAuthorisationStatusPipe,
        ShowErrorsComponent,
        AccountAirspaceDataComponent,
        AirspaceCheckResults,
        AuthorisationOutcomeDetailsDialogue,
        MapAirspaceChecker,
        MapAirspaceParametersComponent
    ],
    exports: [
        AirspaceRuleDetailsDialogue,
        AirspaceRuleMessageDialogue,
        AirspaceDisclaimerDialogue,
        AirspaceNotificationCard,
        AirspaceCheck,
        AirspaceAuthorisationPreviewDialogue,
        AirspaceConsentForTrialDialogue,
        AirspaceAuthorisationSummary,
        AirspaceUnavailableAlert,
        AirspaceAuthorisationStatus,
        AirspaceAuthorisationMessagingComponent,
        JurisdictionLinksComponent,
        AirspaceCheckResultDirective,
        FormatAuthorisationEligibilityPipe,
        FormatAuthorisationStatusPipe,
        InlineAirspaceWarningsAndBlockersComponent,
        AirspaceAuthorisationsDirective,
        InlineAuthorisationComponent,
        AccountAirspaceDataComponent,
        AirspaceCheckResults,
        ShowErrorsComponent,
        MapAirspaceChecker,
        MapAirspaceParametersComponent
    ]
})
export class AirspaceModule {}

export {
    AirspaceAuthorisationMessagingComponent,
    AirspaceAuthorisationPreviewDialogue,
    AirspaceAuthorisationStatus,
    AirspaceAuthorisationSummary,
    AirspaceCheck,
    AirspaceCheckResults,
    AirspaceConsentForTrialDialogue,
    AirspaceDisclaimerDialogue,
    AirspaceNotificationCard,
    AirspaceRuleDetailsDialogue,
    AirspaceRuleMessageDialogue,
    AirspaceUnavailableAlert,
    JurisdictionLinksComponent,
    MapAirspaceCheckerService
};
